/**
 * src/components/approvals/UserSubmissionModal.tsx
 * Modal for regular users to submit transaction entries for Admin approval
 */

import React, { useState, useEffect } from 'react';
import { Send, ShieldAlert, Sparkles, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassInput } from '../ui/GlassInput';
import { GlassButton } from '../ui/GlassButton';
import { ReceiptUploadField } from '../ui/ReceiptUploadField';
import { QuickExpensePicker } from '../ledger/QuickExpensePicker';
import { saveRecentPreset, type ExpensePreset } from '../../utils/frequentExpenses';
import type { CategoryItem } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface UserSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<any>;
  categories: CategoryItem[];
}

export const UserSubmissionModal: React.FC<UserSubmissionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  categories,
}) => {
  const { user } = useAuth();

  const [transactionType, setTransactionType] = useState<'paid' | 'receive'>('receive');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('General');
  const [transactionDate, setTransactionDate] = useState<string>('');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Set default timestamp on open
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setTransactionDate(now.toISOString().slice(0, 16));
      setAmount('');
      setDescription('');
      setCategoryName(categories[0]?.name || 'General');
      setReceiptUrl(null);
      setFormError('');
      setTransactionType('receive');
    }
  }, [isOpen, categories]);

  const handleQuickAdd = (chipAmount: number) => {
    const current = parseFloat(amount) || 0;
    setAmount((current + chipAmount).toString());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      setFormError('Please enter a valid amount greater than 0.');
      return;
    }

    if (!description.trim()) {
      setFormError('Please enter a description for this entry.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        transaction_type: transactionType,
        amount: parsedAmount,
        description: description.trim(),
        category_name: categoryName,
        transaction_date: transactionDate ? new Date(transactionDate).toISOString() : new Date().toISOString(),
        receipt_url: receiptUrl || null
      });

      // Save to recent presets for 1-tap quick pick next time
      saveRecentPreset({
        label: description.trim(),
        amount: parsedAmount,
        description: description.trim(),
        categoryName: categoryName || 'General',
        icon: transactionType === 'receive' ? '💰' : '☕'
      });

      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickPresetSelect = (preset: ExpensePreset) => {
    setAmount(String(preset.amount));
    setDescription(preset.description);
    if (preset.categoryName) {
      const matched = categories.find(c =>
        c.name.toLowerCase().includes(preset.categoryName.toLowerCase()) ||
        preset.categoryName.toLowerCase().includes(c.name.toLowerCase())
      );
      if (matched) {
        setCategoryName(matched.name);
      } else {
        setCategoryName(preset.categoryName);
      }
    }
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Submit Entry for Approval"
      maxWidth="540px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {formError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f87171',
            fontSize: '0.875rem'
          }}>
            {formError}
          </div>
        )}

        {/* Transaction Type: Paid by Me (User Paid) / Received by Me (User Received) */}
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
            Transaction Type (from Your perspective)
          </label>
          <div className="ios-segmented-control" style={{ width: '100%', padding: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <button
              type="button"
              onClick={() => setTransactionType('receive')}
              className={`ios-segment-button ${transactionType === 'receive' ? 'active' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '9px 12px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: transactionType === 'receive' ? 'var(--ios-green)' : 'var(--text-secondary)'
              }}
            >
              <ArrowUpRight size={15} strokeWidth={2.5} />
              Paid by Me (Credit +)
            </button>
            <button
              type="button"
              onClick={() => setTransactionType('paid')}
              className={`ios-segment-button ${transactionType === 'paid' ? 'active' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '9px 12px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: transactionType === 'paid' ? 'var(--ios-red)' : 'var(--text-secondary)'
              }}
            >
              <ArrowDownLeft size={15} strokeWidth={2.5} />
              Received by Me (Debit -)
            </button>
          </div>
          <div style={{
            fontSize: '0.74rem',
            color: 'var(--text-tertiary)',
            marginTop: '6px',
            padding: '4px 6px',
            borderRadius: '6px',
            background: 'rgba(118, 118, 128, 0.08)'
          }}>
            {transactionType === 'receive'
              ? '🟢 Paid by Me: You spent money out-of-pocket / paid company → Credits your balance (+)'
              : '🔴 Received by Me: You received money / funds from company → Debits your balance (-)'}
          </div>
        </div>

        {/* Quick Selection for Recent & Frequent Presets */}
        <QuickExpensePicker
          onSelect={handleQuickPresetSelect}
          selectedDescription={description}
          selectedAmount={amount}
        />

        {/* Amount Input with Currency Symbol */}
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
            Amount (₹)
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: transactionType === 'receive' ? 'var(--ios-green)' : 'var(--ios-red)',
              pointerEvents: 'none'
            }}>
              ₹
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="glass-input num-font"
              style={{
                paddingLeft: '34px',
                fontSize: '1.35rem',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}
            />
          </div>

          {/* Quick Increment Chips - iOS Pills */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            {[50, 100, 200, 500, 1000].map(val => (
              <button
                key={val}
                type="button"
                onClick={() => handleQuickAdd(val)}
                style={{
                  background: 'rgba(118, 118, 128, 0.18)',
                  border: 'var(--border-hairline)',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-pill)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                +₹{val}
              </button>
            ))}
          </div>
        </div>

        {/* Category & Date Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Category
            </label>
            <select
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              className="glass-select"
              style={{ width: '100%' }}
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.icon || '🏷️'} {cat.name}
                </option>
              ))}
              <option value="General">🏷️ General</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Date & Time
            </label>
            <input
              type="datetime-local"
              required
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              className="glass-input"
              style={{ fontSize: '0.85rem' }}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            DESCRIPTION / PURPOSE
          </label>
          <input
            type="text"
            required
            placeholder="e.g. Lunch with team, Groceries, Project supplies"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-glass)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none'
            }}
          />
        </div>

        {/* Receipt Attachment Upload */}
        <ReceiptUploadField
          receiptUrl={receiptUrl}
          onChange={setReceiptUrl}
          onParsed={(data) => {
            if (data.amount && (!amount || amount === '0')) {
              setAmount(data.amount.toString());
            }
            if (data.suggestedCategory) {
              setCategoryName(data.suggestedCategory);
            }
            if (data.suggestedDescription && !description) {
              setDescription(data.suggestedDescription);
            }
          }}
          disabled={isSubmitting}
        />

        {/* Informational Approval Notice Box */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          padding: '0.875rem',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(56, 189, 248, 0.08)',
          border: '1px solid rgba(56, 189, 248, 0.25)',
          color: '#38bdf8',
          fontSize: '0.8125rem',
          lineHeight: 1.45
        }}>
          <ShieldAlert size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Admin Verification Required:</strong> This entry will be placed in the approval queue. Your account balance will update automatically as soon as the Admin approves it.
          </div>
        </div>

        {/* Modal Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
          <GlassButton
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </GlassButton>
          <GlassButton type="submit" variant="primary" disabled={isSubmitting}>
            <Send size={16} />
            {isSubmitting ? 'Submitting...' : 'Submit Entry'}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
